import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useLocalData } from "../hooks/useLocalData";
import {
  onOrgTeam, onOrgNotes, onOrgSessions, onOrgIndividuals,
} from "../services/firestoreSync";
import { signNote, requestRevision, updateUserRole } from "../services/teamService";
import {
  onSafetyAlerts, acknowledgeAlert, reviewAlert, resolveAlert,
  requestNotificationPermission, playAlertSound, notifySupervisor, sendBrowserNotification,
} from "../services/safetyAlertService";
import {
  Users, FileText, Shield, DollarSign, BarChart3,
  AlertTriangle, Settings, UserCheck, ClipboardList, AlertOctagon, UserPlus,
} from "lucide-react";

// Tab components
import OverviewTab from "../components/admin/OverviewTab";
import SafetyTab from "../components/admin/SafetyTab";
import NotesTab from "../components/admin/NotesTab";
import TeamTab from "../components/admin/TeamTab";
import CaseloadTab from "../components/admin/CaseloadTab";
import BillingTab from "../components/admin/BillingTab";
import ReportsTab from "../components/admin/ReportsTab";
import ComplianceTab from "../components/admin/ComplianceTab";
import IntakeTab from "../components/admin/IntakeTab";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { notes = [], individuals = [] } = useLocalData();

  const [activeTab, setActiveTab] = useState("overview");

  // Firestore state
  const [teamMembers, setTeamMembers] = useState([]);
  const [sharedNotes, setSharedNotes] = useState([]);
  const [sharedSessions, setSharedSessions] = useState([]);
  const [sharedIndividuals, setSharedIndividuals] = useState([]);
  const [safetyAlertsLive, setSafetyAlertsLive] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const prevAlertCount = useRef(0);
  const prevNoteCount = useRef(-1);

  // Shared modal state
  const [revisionNote, setRevisionNote] = useState(null);
  const [revisionText, setRevisionText] = useState("");

  const orgId = profile?.organization || "Highland Rivers Health";
  const supervisorName = profile?.displayName || user?.displayName || user?.email || "Supervisor";

  // Firestore subscriptions
  useEffect(() => {
    if (!orgId) return;
    setTeamLoading(true);
    requestNotificationPermission();

    const unsubTeam = onOrgTeam(orgId, (members) => { setTeamMembers(members); setTeamLoading(false); });
    const unsubSessions = onOrgSessions(orgId, (s) => setSharedSessions(s));
    const unsubIndividuals = onOrgIndividuals(orgId, (inds) => setSharedIndividuals(inds));
    const unsubSafety = onSafetyAlerts(orgId, (alerts) => {
      const prevCount = prevAlertCount.current;
      const activeAlerts = alerts.filter(a => a.status === "active");
      
      if (activeAlerts.length > prevCount && prevCount >= 0) {
        playAlertSound();
        // Send browser notification for each NEW alert
        const newAlerts = activeAlerts.slice(0, activeAlerts.length - prevCount);
        newAlerts.forEach(a => notifySupervisor(a));
      }
      prevAlertCount.current = activeAlerts.length;
      setSafetyAlertsLive(alerts);
    });

    // Notify on new note submissions
    const unsubNotes = onOrgNotes(orgId, (n) => {
      if (prevNoteCount.current >= 0 && n.length > prevNoteCount.current) {
        const newest = n[0];
        if (newest && newest.status === "submitted") {
          sendBrowserNotification(
            "📝 New Note Submitted",
            `${newest.agentName || "Agent"} submitted a note for ${newest.individualName || "individual"}`,
            `note-${newest.id}`
          );
        }
      }
      prevNoteCount.current = n.length;
      setSharedNotes(n);
    });

    return () => { unsubTeam(); unsubNotes(); unsubSessions(); unsubIndividuals(); unsubSafety(); };
  }, [orgId]);

  // Derived data
  const allNotes = useMemo(() => {
    const localIds = new Set(notes.map(n => n.id));
    return [...notes, ...sharedNotes.filter(n => !localIds.has(n.id))];
  }, [notes, sharedNotes]);

  const submittedNotes = allNotes.filter(n => n.status === "submitted" || n.status === "signed");
  const draftNotes = allNotes.filter(n => n.status === "draft");
  const pendingReview = submittedNotes.filter(n => !n.supervisorReviewed);
  const todayNotes = allNotes.filter(n => n.date === new Date().toLocaleDateString());
  const activeAlerts = safetyAlertsLive.filter(a => a.status === "active");
  const safetyAlerts = allNotes.filter(n => n.hasPriorityFlags);
  const unreviewedAlerts = activeAlerts.length;
  const avgConfidence = submittedNotes.length > 0
    ? Math.round(submittedNotes.reduce((s, n) => s + (n.confidence || 0), 0) / submittedNotes.length) : 0;

  const agents = teamMembers.filter(m => m.role === "agent");
  const counselors = teamMembers.filter(m => m.role === "counselor");
  const supervisors = teamMembers.filter(m => m.role === "supervisor" || m.role === "admin");
  const inSession = teamMembers.filter(m => m.liveStatus === "in-session");

  // Handlers
  const handleSignNote = async (noteId) => await signNote(noteId, supervisorName);
  const handleRequestRevision = async () => {
    if (!revisionNote || !revisionText.trim()) return;
    await requestRevision(revisionNote.id, revisionText.trim(), supervisorName);
    setRevisionNote(null); setRevisionText("");
  };
  const handleAcknowledgeAlert = async (id) => await acknowledgeAlert(id, supervisorName);
  const handleReviewAlert = async (id, notes) => await reviewAlert(id, supervisorName, notes || "");
  const handleResolveAlert = async (id, n) => await resolveAlert(id, supervisorName, n);
  const handleRoleChange = async (uid, role) => await updateUserRole(uid, role);

  const tabs = [
    { key: "overview", label: "Overview", Icon: BarChart3 },
    { key: "safety", label: "Safety", Icon: Shield, badge: unreviewedAlerts },
    { key: "notes", label: "Notes", Icon: FileText, badge: pendingReview.length },
    { key: "team", label: "Team", Icon: Users, badge: inSession.length },
    { key: "caseload", label: "Caseload", Icon: UserCheck },
    { key: "intake", label: "Intake", Icon: UserPlus },
    { key: "compliance", label: "Compliance", Icon: AlertOctagon },
    { key: "billing", label: "Billing", Icon: DollarSign },
    { key: "reports", label: "Reports", Icon: ClipboardList },
  ];

  const C = {
    card: { background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)", borderRadius: 16, padding: 20, marginBottom: 16 },
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      {/* Header */}
      <header style={{
        padding: "16px 20px", background: "linear-gradient(135deg, #0f1729, #162033)",
        borderBottom: "1px solid rgba(99,179,237,0.1)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "linear-gradient(135deg, #63b3ed, #4fd1c5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, fontWeight: 900, color: "#0a0f1e",
          }}>F</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>FieldNotes</div>
            <div style={{ fontSize: 10, color: "#718096", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>
              {orgId !== "default-org" ? orgId : "Supervisor Dashboard"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {unreviewedAlerts > 0 && (
            <button onClick={() => setActiveTab("safety")} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 10,
              background: "rgba(252,129,129,0.1)", border: "1px solid rgba(252,129,129,0.2)",
              color: "#fc8181", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              <AlertTriangle size={12} /> {unreviewedAlerts} Alert{unreviewedAlerts > 1 ? "s" : ""}
            </button>
          )}
          <div style={{ fontSize: 12, color: "#718096" }}>{supervisorName}</div>
          <button onClick={() => navigate("/settings")} style={{
            width: 32, height: 32, borderRadius: 8, background: "rgba(99,179,237,0.06)",
            border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Settings size={16} style={{ color: "#718096" }} />
          </button>
          <button onClick={() => navigate("/dashboard")} style={{
            fontSize: 12, color: "#63b3ed", fontWeight: 600, background: "none", border: "none", cursor: "pointer",
          }}>Agent View →</button>
        </div>
      </header>

      {/* Tab Nav */}
      <div style={{
        display: "flex", gap: 4, padding: "12px 20px", overflowX: "auto",
        background: "rgba(15,23,42,0.5)", borderBottom: "1px solid rgba(99,179,237,0.06)",
      }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10,
            background: activeTab === t.key ? "rgba(99,179,237,0.12)" : "transparent",
            color: activeTab === t.key ? "#63b3ed" : "#718096",
            border: `1px solid ${activeTab === t.key ? "rgba(99,179,237,0.2)" : "transparent"}`,
            fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
          }}>
            <t.Icon size={14} /> {t.label}
            {(t.badge || 0) > 0 && (
              <span style={{
                minWidth: 18, height: 18, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 800, background: t.key === "safety" ? "#fc8181" : "rgba(99,179,237,0.2)", color: t.key === "safety" ? "white" : "#63b3ed",
              }}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <main style={{ padding: 20 }}>
        {activeTab === "overview" && <OverviewTab C={C} teamMembers={teamMembers} teamLoading={teamLoading} inSession={inSession} unreviewedAlerts={unreviewedAlerts} allNotes={allNotes} todayNotes={todayNotes} pendingReview={pendingReview} avgConfidence={avgConfidence} safetyAlerts={safetyAlerts} setActiveTab={setActiveTab} />}
        {activeTab === "safety" && <SafetyTab C={C} allSafetyAlerts={safetyAlertsLive} teamMembers={teamMembers} handleAcknowledgeAlert={handleAcknowledgeAlert} handleReviewAlert={handleReviewAlert} handleResolveAlert={handleResolveAlert} />}
        {activeTab === "notes" && <NotesTab C={C} allNotes={allNotes} submittedNotes={submittedNotes} draftNotes={draftNotes} pendingReview={pendingReview} safetyAlerts={safetyAlerts} handleSignNote={handleSignNote} setRevisionNote={setRevisionNote} supervisorName={supervisorName} />}
        {activeTab === "team" && <TeamTab C={C} teamMembers={teamMembers} teamLoading={teamLoading} sharedNotes={sharedNotes} orgId={orgId} agents={agents} counselors={counselors} supervisors={supervisors} handleRoleChange={handleRoleChange} sharedIndividuals={sharedIndividuals} />}
        {activeTab === "caseload" && <CaseloadTab C={C} sharedIndividuals={sharedIndividuals} allNotes={allNotes} teamMembers={teamMembers} user={user} profile={profile} />}
        {activeTab === "intake" && <IntakeTab />}
        {activeTab === "billing" && <BillingTab C={C} submittedNotes={submittedNotes} allNotes={allNotes} individuals={sharedIndividuals} teamMembers={teamMembers} orgId={orgId} />}
        {activeTab === "compliance" && <ComplianceTab C={C} sharedIndividuals={sharedIndividuals} allNotes={allNotes} teamMembers={teamMembers} />}
        {activeTab === "reports" && <ReportsTab C={C} allNotes={allNotes} teamMembers={teamMembers} sharedIndividuals={sharedIndividuals} safetyAlerts={safetyAlertsLive} orgId={orgId} />}
      </main>

      {/* Revision Modal */}
      {revisionNote && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={() => setRevisionNote(null)}>
          <div style={{ ...C.card, maxWidth: 500, width: "90%", padding: 24 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Request Revision</div>
            <div style={{ fontSize: 12, color: "#718096", marginBottom: 16 }}>{revisionNote.individualName} — {revisionNote.agentName || "Agent"}</div>
            <textarea value={revisionText} onChange={e => setRevisionText(e.target.value)} placeholder="Describe what needs to be changed..."
              style={{ width: "100%", padding: 12, borderRadius: 10, minHeight: 100, resize: "none", background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 13, outline: "none" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={handleRequestRevision} style={{ flex: 1, padding: 10, borderRadius: 10, fontSize: 13, fontWeight: 700, background: "linear-gradient(135deg, #dd6b20, #c05621)", color: "white", border: "none", cursor: "pointer" }}>Send Revision Request</button>
              <button onClick={() => setRevisionNote(null)} style={{ padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, background: "transparent", color: "#718096", border: "1px solid rgba(99,179,237,0.06)", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}